import {
  configureStore,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { ProductDetailsModal } from "../../interfaces/ProductObject";
import { sampleProductsDictionary } from "../../dummyData/SampleProductList";
import { data } from "react-router-dom";
import ProductListThunks, {
  loadProducts,
} from "../../services/thunks/ProductListThunks";
import genericUtility from "../../services/utility/GenericUtility";
import { resetAllState } from "./resetAllState";

// Define the initial state as a dictionary with productId as keys and ProductDetails as values
type ProductListState = Record<string, ProductDetailsModal>;

const initialState: ProductListState = {};

// Create a slice for ProductListDb
const productListSlice = createSlice({
  name: "ProductListDb",
  initialState: {
    data: initialState,
    loadingMap: {} as any,
    errorMap: {} as any,
    successMap: {} as any,
    metadata: {
      localFindsProducts: false,
      searchResults: false,
      searchString: "",
      searchInfo: false,
    },
  },
  reducers: {
    // Action to add or update a product in the store
    insertProduct(
      state,
      action: PayloadAction<{
        productId: string;
        productDetail: ProductDetailsModal;
      }>
    ) {
      const { productId, productDetail } = action.payload;
      state.data[productId] = productDetail;
    },
    // Action to remove a product from the store
    removeProduct(state, action: PayloadAction<string>) {
      delete state.data[action.payload];
    },
    // Action to clear all products from the store
    clearProducts(state) {
      state.data = {};
      state.loadingMap = {};
      state.errorMap = {};
    },
    setSearchResults(state) {
      state.metadata.searchResults = true;
      state.metadata.localFindsProducts = false;
    },
    setLocalFinds(state) {
      state.metadata.searchResults = false;
      state.metadata.localFindsProducts = true;
    },
    setSearchString(state, action: PayloadAction<string>) {
      state.metadata.searchString = action.payload;
    },
    setSearchInfo(state, action: PayloadAction<boolean>) {
      state.metadata.searchInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ProductListThunks.search.pending, (state) => {
        state.loadingMap.search = "Loading the search results...";
        state.successMap.search = null;
        state.errorMap.search = null;
      })
      .addCase(ProductListThunks.search.fulfilled, (state, action) => {
        state.loadingMap.search = null;
        state.data = action.payload as ProductListState;
        state.metadata.searchResults = true;
        state.metadata.localFindsProducts = false;
        state.successMap.search = "Search completed successfully!";
      })
      .addCase(ProductListThunks.search.rejected, (state, action) => {
        state.loadingMap.search = null;
        state.successMap.search = null;
        state.errorMap.search = "Error searching products";
      })
      .addCase(ProductListThunks.categorySearch.pending, (state) => {
        state.loadingMap.search = "Loading category products...";
        state.successMap.search = null;
        state.errorMap.search = null;
        state.metadata.searchString = ""; // Category search clears any text query
      })
      .addCase(ProductListThunks.categorySearch.fulfilled, (state, action) => {
        state.loadingMap.search = null;
        state.data = action.payload as ProductListState;
        state.metadata.searchResults = true;
        state.metadata.localFindsProducts = false;
        state.successMap.search = "Search completed successfully!";
      })
      .addCase(ProductListThunks.categorySearch.rejected, (state, action) => {
        state.loadingMap.search = null;
        state.successMap.search = null;
        state.errorMap.search = "Error searching category products";
      })
      .addCase(ProductListThunks.localFindsSearch.pending, (state) => {
        state.loadingMap.localFindsSearch = "Finding nearby products...";
        state.successMap.localFindsSearch = null;
        state.errorMap.localFindsSearch = null;
      })
      .addCase(ProductListThunks.localFindsSearch.fulfilled, (state) => {
        state.loadingMap.localFindsSearch = null;
        state.successMap.localFindsSearch = "Local finds loaded!";
      })
      .addCase(ProductListThunks.localFindsSearch.rejected, (state) => {
        state.loadingMap.localFindsSearch = null;
        state.successMap.localFindsSearch = null;
        state.errorMap.localFindsSearch = "Couldn't load local finds. Please try again.";
      })
      .addCase(ProductListThunks.loadProducts.pending, (state) => {
        state.loadingMap.loadProducts = "Loading the products";
        state.errorMap.loadProducts = null;
        state.successMap.loadProducts = null;
      })
      .addCase(ProductListThunks.loadProducts.fulfilled, (state, action) => {
        state.loadingMap.loadProducts = null;
        state.successMap.loadProducts = "Products loaded successfully!";
        const newProducts = action.payload as ProductListState;
        // for each product use insert product reducer to update the existing product
        state.data = newProducts; // Merge new products into existing state
        // Object.entries(newProducts).forEach(([productId, productDetail]) => {
        //   insertProduct(state, { productId, productDetail });
        // });
      })
      .addCase(ProductListThunks.loadProducts.rejected, (state, action) => {
        state.loadingMap.loadProducts = null;
        state.successMap.loadProducts = null;
        state.errorMap.loadProducts = "Error loading products";
      })
      .addCase(resetAllState, () => ({
        data: {},
        loadingMap: {},
        errorMap: {},
        successMap: {},
        metadata: {
          localFindsProducts: false,
          searchResults: false,
          searchString: "",
          searchInfo: false,
        },
      }));
  },
});

// Export actions
export const {
  insertProduct,
  removeProduct,
  clearProducts,
  setSearchResults,
  setLocalFinds,
  setSearchString,
  setSearchInfo,
} = productListSlice.actions;
export default productListSlice.reducer;
